if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');
        this.pendingPostAddOffer = null;
        this.submitLabelResetTimeout = null;
        this.submitSucceeded = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.pendingPostAddOffer = null;
        this.submitSucceeded = false;
        if (this.submitLabelResetTimeout) window.clearTimeout(this.submitLabelResetTimeout);
        this.submitButton.dataset.finchSubmitDefaultLabel =
          this.submitButton.dataset.addLabel || this.submitButtonText?.textContent?.trim() || '';

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.submitButtonText && this.submitButton.dataset.loadingLabel) {
          this.submitButtonText.textContent = this.submitButton.dataset.loadingLabel;
        }
        this.loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const submitCartAdd = async () => {
          const request = await fetch(`${routes.cart_add_url}`, config);
          const response = await request.json();
          await this.handleCartAddResponse(response, formData, request.ok);
          return response;
        };
        const cartAddRequest =
          this.cart && typeof this.cart.enqueueCartMutation === 'function'
            ? this.cart.enqueueCartMutation(submitCartAdd)
            : submitCartAdd();

        cartAddRequest
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.loadingSpinner?.classList.add('hidden');

            if (this.submitSucceeded) {
              this.submitLabelResetTimeout = window.setTimeout(() => this.restoreSubmitButtonLabel(), 1200);
            } else {
              this.restoreSubmitButtonLabel();
            }

            this.openPendingPostAddOfferAfterPaint();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async handleCartAddResponse(response, formData, requestOk = true) {
        if (!requestOk || response.status || response.errors) {
          await publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          const errorMessage =
            response.description ||
            response.message ||
            (typeof response.errors === 'string' ? response.errors : false);
          this.handleErrorMessage(errorMessage);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        this.submitSucceeded = true;
        if (this.submitButtonText && this.submitButton.dataset.addedLabel) {
          this.submitButtonText.textContent = this.submitButton.dataset.addedLabel;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        }

        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.renderCartResponse(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
          return;
        }

        CartPerformance.measure('add:paint-updated-sections', () => {
          this.renderCartResponse(response);
        });
      }

      renderCartResponse(response) {
        const postAddOffer = document.querySelector('finch-post-add-offer');
        const shouldOpenPostAddOffer =
          this.hasAttribute('data-finch-main-product-form') &&
          postAddOffer?.canOpen?.({ productForm: this });

        if (shouldOpenPostAddOffer) {
          this.cart.renderContents(response, { openDrawer: false });
          this.pendingPostAddOffer = {
            postAddOffer,
            cartData: response,
            cartDrawer: this.cart,
            triggerElement: this.submitButton,
            productForm: this,
          };
          return;
        }

        this.cart.renderContents(response);
      }

      restoreSubmitButtonLabel() {
        if (!this.submitButtonText) return;

        const defaultLabel =
          this.submitButton.dataset.addLabel || this.submitButton.dataset.finchSubmitDefaultLabel;
        if (defaultLabel) this.submitButtonText.textContent = defaultLabel;
      }

      openPendingPostAddOfferAfterPaint() {
        const pendingOffer = this.pendingPostAddOffer;
        this.pendingPostAddOffer = null;
        if (!pendingOffer) return;

        const openOffer = () => {
          const opened = pendingOffer.postAddOffer.open({
            cartData: pendingOffer.cartData,
            cartDrawer: pendingOffer.cartDrawer,
            triggerElement: pendingOffer.triggerElement,
            productForm: pendingOffer.productForm,
          });

          if (!opened) pendingOffer.cartDrawer.open?.(pendingOffer.triggerElement);
        };

        if (typeof window.requestAnimationFrame !== 'function') {
          window.setTimeout(openOffer, 0);
          return;
        }

        window.requestAnimationFrame(() => window.requestAnimationFrame(openOffer));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
